import dayjs from 'dayjs';

/**
 * Check if device is touch-enabled
 */
export const isTouchDevice = () => {
    return (
        'ontouchstart' in window ||
        navigator.maxTouchPoints > 0 ||
        navigator.msMaxTouchPoints > 0
    );
};

/**
 * Check if device is in portrait orientation
 */
export const isPortrait = () => {
    return window.innerHeight > window.innerWidth;
};

/**
 * Get viewport dimensions with safe area insets
 */
export const getViewportDimensions = () => {
    const safeAreaTop = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--sat') || '0');
    const safeAreaBottom = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--sab') || '0');

    return {
        width: window.innerWidth,
        height: window.innerHeight,
        safeHeight: window.innerHeight - safeAreaTop - safeAreaBottom,
        safeAreaTop,
        safeAreaBottom
    };
};

/**
 * Group events by hour for mobile timeline view
 */
export const groupEventsByHour = (events) => {
    const grouped = {};

    events.forEach(event => {
        const hour = dayjs(event.startTime).format('HH:00');
        if (!grouped[hour]) {
            grouped[hour] = [];
        }
        grouped[hour].push(event);
    });

    return grouped;
};

/**
 * Calculate appropriate event height for mobile display
 */
export const calculateMobileEventHeight = (durationMinutes, baseHeight = 60) => {
    const minHeight = 50; // Minimum height for touch targets
    const calculatedHeight = (durationMinutes / 60) * baseHeight;
    return Math.max(calculatedHeight, minHeight);
};

/**
 * Check if two time ranges overlap (for mobile event grouping)
 */
export const doTimesOverlap = (start1, end1, start2, end2) => {
    return dayjs(start1).isBefore(dayjs(end2)) && dayjs(end1).isAfter(dayjs(start2));
};

/**
 * Get contrasting text color based on background
 */
export const getContrastTextColor = (backgroundColor) => {
    // Convert hex to RGB
    const hex = backgroundColor.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);

    // Calculate luminance
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

    return luminance > 0.5 ? '#000000' : '#ffffff';
};

/**
 * Debounce function for touch events
 */
export const debounce = (func, wait) => {
    let timeout;
    return function executedFunction(...args) {
        const later = () => {
            clearTimeout(timeout);
            func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
    };
};

/**
 * Format event duration for mobile display
 */
export const formatMobileDuration = (startTime, endTime) => {
    const start = dayjs(startTime);
    const end = dayjs(endTime);
    const minutes = end.diff(start, 'minute');

    if (minutes < 60) {
        return `${minutes} min`;
    } else if (minutes === 60) {
        return '1 godz';
    } else {
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;
        return mins > 0 ? `${hours} godz ${mins} min` : `${hours} godz`;
    }
};

/**
 * Get mobile-friendly event summary
 */
export const getMobileEventSummary = (event) => {
    if (event.confirmed && event.dogDTO) {
        return event.dogDTO.name;
    } else if (event.trainingGroup) {
        return event.trainingGroup.name;
    } else if (event.isLocked) {
        return event.isTemporary ? 'Nowa blokada' : 'Zablokowane';
    } else if (event.bookingCount > 0) {
        return `${event.bookingCount} oczek.`;
    }
    return 'Wolny termin';
};

/**
 * Determine if scrolling should be locked (e.g., during drag operations)
 */
export const lockBodyScroll = (lock = true) => {
    if (lock) {
        document.body.style.overflow = 'hidden';
        document.body.style.position = 'fixed';
        document.body.style.width = '100%';
    } else {
        document.body.style.overflow = '';
        document.body.style.position = '';
        document.body.style.width = '';
    }
};

/**
 * Check if element is in viewport
 */
export const isElementInViewport = (element) => {
    const rect = element.getBoundingClientRect();
    return (
        rect.top >= 0 &&
        rect.left >= 0 &&
        rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
        rect.right <= (window.innerWidth || document.documentElement.clientWidth)
    );
};